import { useMutation } from '@apollo/client'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'

import {
  GetCustomerCartQuery,
  RemoveItemFromCartDocument,
  UpdateCartItemsDocument,
} from '../../graphql/magento'
import { TokenContext } from '../../layouts/context'
import { setAnalyticsEvent } from '../../lib/analytics/state'
import { parseItemIdAsNumber } from '../../lib/cart/item'
import { useCartItemsUpdating } from '../../lib/cart/state/cart-items/cart-item-update'
import { CartItemVirtualProduct } from '../../lib/cart/state/types'
import { CartItem as CartItemType } from '../../lib/cart/types'
import { logAndCaptureException } from '../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../utils/graphqlTools'
import { CartItemProps } from './types'

const randomRequestId = (itemId: string) => `${itemId}-${Math.random().toString(32).substr(2, 8)}`

const publishRemoveFromCartEvent = (item: CartItemType) => () => {
  const sku = item.product.sku
  const rowTotal = item.prices?.row_total
  const currency = rowTotal?.currency
  const value = rowTotal?.value
  if (!sku || !currency || value == null) {
    return
  }
  setAnalyticsEvent({
    event: 'remove_from_cart',
    currency,
    value,
    items: [
      {
        item_id: sku,
        item_name: item.product.name ?? undefined,
        currency,
        price: item.prices?.price?.value ?? undefined,
        quantity: item.quantity,
      },
    ],
  })
}

export interface CartItemContainerProps {
  className?: string
  cartId: string
  component: React.ElementType<CartItemProps>
  item: CartItemType
  labelDesignFeeItem?: CartItemVirtualProduct
  requestDispatch(action: { type: 'requestPending' | 'requestComplete'; requestId: string }): void
  shippingAddress: GetCustomerCartQuery['customerCart']['shipping_addresses'][0] | undefined
}

const CartItemContainer: React.FC<CartItemContainerProps> = ({
  cartId,
  className,
  component,
  item,
  labelDesignFeeItem,
  requestDispatch,
  shippingAddress,
}) => {
  const token = useContext(TokenContext)
  const cartItemsUpdating = useCartItemsUpdating()

  const isCartItemUpdating = useMemo(
    () => cartItemsUpdating.some((sku) => sku === item?.product?.sku),
    [cartItemsUpdating, item?.product?.sku],
  )

  const [updateCartItems, { loading, error: mutationError, client: mutationClient }] = useMutation(
    UpdateCartItemsDocument,
    {
      context: { token },
    },
  )

  useEffect(() => {
    if (mutationError?.graphQLErrors) {
      mutationClient.cache.evict({ id: `Cart:${cartId}`, fieldName: 'items' })
    }
  }, [cartId, mutationClient.cache, mutationError])

  const [deleteItem, deleteOptions] = useMutation(RemoveItemFromCartDocument, {
    context: { token },
  })

  const handleDelete = useCallback(async (): Promise<void> => {
    const requestId = randomRequestId(item.id)
    // inform parent this request is pending
    requestDispatch({ type: 'requestPending', requestId })

    return Promise.resolve()
      .then(() => parseItemIdAsNumber(item))
      .then((itemId) =>
        deleteItem({
          variables: {
            cartId,
            itemId,
          },
        }),
      )
      .then(
        // check GraphQL response for errors
        checkAndDiscardQueryResponse,
      )
      .then(publishRemoveFromCartEvent(item))
      .catch((rawErr: unknown) => {
        // ensure error is instance of Error
        const err: Error = rawErr instanceof Error ? rawErr : new Error(`handleDelete: ${rawErr}`)
        logAndCaptureException(err)
      })
      .finally(() =>
        // inform parent this request has completed
        requestDispatch({ type: 'requestComplete', requestId }),
      )
  }, [cartId, deleteItem, item, requestDispatch])

  useEffect(() => {
    if (deleteOptions.error?.graphQLErrors) {
      deleteOptions.client.cache.evict({
        id: `Cart:${cartId}`,
        fieldName: 'items',
      })
    }
  }, [cartId, deleteOptions.client.cache, deleteOptions.error?.graphQLErrors])

  // NOTE: consider raising to parent component that can queue updates and submit multiple at once
  const handleUpdateCartItem = useCallback(
    async ({
      autoShipInterval,
      orderType,
      quantity,
    }: {
      autoShipInterval?: number
      orderType?: number
      quantity?: number
    }): Promise<{ error?: Error }> => {
      const requestId = randomRequestId(item.id)
      // inform parent this request is pending
      requestDispatch({ type: 'requestPending', requestId })

      return Promise.resolve()
        .then(() => parseItemIdAsNumber(item))
        .then((itemId) =>
          updateCartItems({
            variables: {
              cartId,
              cartItems: [
                {
                  cart_item_id: itemId,
                  quantity: quantity != null ? quantity : item.quantity,
                  ...(autoShipInterval != null && { dfh_autoship_interval: autoShipInterval }),
                  // TODO: support updating customizable options
                  /*...(customOptions != null && { customizable_options: customOptions }),*/
                  ...(orderType != null && { dfh_order_type: orderType }),
                },
              ],
            },
          }),
        )
        .then(
          // check GraphQL response for errors
          checkAndDiscardQueryResponse,
        )
        .then(() => ({}))
        .catch((rawErr: unknown) => {
          // ensure error is instance of Error
          const err: Error =
            rawErr instanceof Error ? rawErr : new Error(`handleUpdateCartItems: ${rawErr}`)
          logAndCaptureException(err)
          return { error: err }
        })
        .finally(() => {
          // inform parent this request has completed
          requestDispatch({ type: 'requestComplete', requestId })
        })
    },
    [cartId, item, requestDispatch, updateCartItems],
  )

  return React.createElement(component, {
    className,
    deleteCartItem: handleDelete,
    deleteError: deleteOptions.error,
    item,
    labelDesignFeeItem,
    loading: loading || deleteOptions.loading || isCartItemUpdating,
    shippingAddress,
    updateCartItem: handleUpdateCartItem,
  })
}

export default CartItemContainer
